import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useLogin } from '@vb-django/components/login/context/LoginContext';

import type { OpenCheckoutOptions } from '../../Checkout.types';

import DroneCheckout from './DroneCheckout';

export type { OpenCheckoutOptions };

export type DroneCheckoutOpenContextValue = {
  openCheckout: (options?: OpenCheckoutOptions) => void;
  closeCheckout: () => void;
  /** Last step index in the new drone flow (logged-in: 3, guest: 4). */
  stepCount: number;
};

const Context = createContext<DroneCheckoutOpenContextValue | null>(null);

export const DroneCheckoutWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isLogged } = useLogin() ?? { isLogged: false };
  const [isOpen, setIsOpen] = useState(false);
  const [openOptions, setOpenOptions] = useState<OpenCheckoutOptions | null>(null);

  const stepCount = useMemo(() => (isLogged ? 3 : 4), [isLogged]);

  const openCheckout = useCallback((options?: OpenCheckoutOptions) => {
    setOpenOptions(options ?? null);
    setIsOpen(true);
  }, []);

  const closeCheckout = useCallback(() => {
    setIsOpen(false);
    setOpenOptions(null);
  }, []);

  const value = useMemo<DroneCheckoutOpenContextValue>(
    () => ({
      openCheckout,
      closeCheckout,
      stepCount
    }),
    [openCheckout, closeCheckout, stepCount]
  );

  return (
    <Context.Provider value={value}>
      {children}
      <DroneCheckout
        isOpen={isOpen}
        onClose={closeCheckout}
        openOptions={openOptions}
        onConsumedOpenOptions={() => setOpenOptions(null)}
      />
    </Context.Provider>
  );
};

export const useOpenDroneCheckout = (): DroneCheckoutOpenContextValue => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useOpenDroneCheckout must be used within DroneCheckoutWrapper');
  }
  return ctx;
};

export const useOpenDroneCheckoutOptional = (): DroneCheckoutOpenContextValue | null =>
  useContext(Context);
